import { z } from 'zod';

/**
 * The standard error envelope.
 *
 * Source: Technical Architecture §21 — "stable code, message, correlation ID,
 * field errors and remediation".
 *
 * `remediation` is the field that distinguishes this from a generic error shape.
 * The product's users are stewards and analysts correcting spreadsheets, not
 * engineers reading stack traces; an error that does not say what to do next has
 * failed at its job.
 */

export const ErrorCode = z.enum([
  // Request / validation
  'VALIDATION_FAILED',
  'SCHEMA_MISMATCH',
  'UNSUPPORTED_TEMPLATE_VERSION',
  // Authorization — deliberately coarse so a denial never leaks existence
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'OUT_OF_SCOPE',
  // Concurrency and workflow
  'VERSION_CONFLICT',
  'PUBLISHED_IMMUTABLE',
  'WORKFLOW_STATE_INVALID',
  'SELF_APPROVAL_BLOCKED',
  // Relationship integrity
  'INVALID_ENDPOINT_PAIR',
  'HIERARCHY_CYCLE',
  'CARDINALITY_EXCEEDED',
  'ALLOCATION_EXCEEDED',
  'EFFECTIVE_DATE_OVERLAP',
  // Data / source
  'SOURCE_UNAVAILABLE',
  'STALE_SOURCE',
  'SOURCE_CONFLICT',
  // Generic
  'NOT_FOUND',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

/**
 * A single field-level problem. `ruleCode` ties back to the governed validation
 * catalog (VAL-001…VAL-015) so an exception workbook row and an API error speak
 * the same language.
 */
export const FieldError = z.object({
  /** Dotted path, or `sheet!A12` style reference for spreadsheet imports. */
  path: z.string().min(1),
  message: z.string().min(1),
  ruleCode: z.string().nullable(),
  /** Echoed back so the user can see what the system actually received. */
  submittedValue: z.string().nullable(),
  remediation: z.string().nullable(),
});
export type FieldError = z.infer<typeof FieldError>;

export const ErrorEnvelope = z.object({
  code: ErrorCode,
  message: z.string().min(1),
  /** Ties the response to logs, audit events and job records. */
  correlationId: z.string().min(1),
  fieldErrors: z.array(FieldError),
  /** What the user should do next. Rendered in the error state, not swallowed. */
  remediation: z.string().nullable(),
  /** Present on VERSION_CONFLICT so the client can offer a reload/merge. */
  expectedRowVersion: z.number().int().nullable(),
  actualRowVersion: z.number().int().nullable(),
  occurredAt: z.string().datetime({ offset: true }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

export class PviError extends Error {
  readonly envelope: ErrorEnvelope;

  constructor(envelope: ErrorEnvelope) {
    super(envelope.message);
    this.name = 'PviError';
    this.envelope = envelope;
  }
}

/**
 * Denial responses are intentionally uniform.
 *
 * `FORBIDDEN` and `OUT_OF_SCOPE` must not differ in a way that lets a caller
 * distinguish "this exists but you may not see it" from "this does not exist" —
 * UX-S07 requires that an unauthorized response reveal neither existence, names
 * nor counts. The helper exists so no route hand-rolls a chattier message.
 */
export function denied(correlationId: string, occurredAt: string): ErrorEnvelope {
  return {
    code: 'FORBIDDEN',
    message: 'You do not have access to this resource.',
    correlationId,
    fieldErrors: [],
    remediation: 'Request access from your administrator if you believe this is an error.',
    expectedRowVersion: null,
    actualRowVersion: null,
    occurredAt,
  };
}
